import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/** A top-layer modal makes the entire workspace inert for the mutation's lifetime. */
export function DocumentOperationProgress({
  operation,
}: {
  operation: "save" | "publish";
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current!,
      opener = document.activeElement as HTMLElement | null;
    element.showModal();
    element.focus();
    const leave = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener("beforeunload", leave);
    window.addEventListener("keydown", escape, true);
    return () => {
      window.removeEventListener("beforeunload", leave);
      window.removeEventListener("keydown", escape, true);
      element.close();
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);
  const label = operation === "save" ? "Saving draft" : "Publishing document";
  return createPortal(
    <dialog
      ref={dialog}
      aria-label={label}
      aria-modal="true"
      tabIndex={-1}
      onCancel={(event) => event.preventDefault()}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100dvh",
        maxWidth: "none",
        maxHeight: "none",
        boxSizing: "border-box",
        margin: 0,
        border: 0,
        padding: 24,
        background: "var(--ps-neutral-1,#fff)",
        color: "var(--ps-neutral-12,#182f32)",
        display: "grid",
        placeItems: "center",
      }}
    >
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        style={{ maxWidth: 560, textAlign: "center" }}
      >
        <h2>{label}…</h2>
        <progress aria-label={`${label} progress`} />
        <p>
          {operation === "save"
            ? "Saving your project changes."
            : "Saving changes and creating the published version."}
        </p>
        <p>Please keep this page open until the operation completes.</p>
      </div>
    </dialog>,
    document.body,
  );
}
